"use client";

import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "motion/react";
import FadeIn from "@/components/fade-in";
import { Cormorant_Garamond } from "next/font/google";
import Image from "next/image";

const displaySerif = Cormorant_Garamond({ subsets: ["latin"], weight: ["500", "600", "700"] });

const pillars = [
  { title: "Total discretion", copy: "An intimate setting with no public exposure — designed for real, safe conversations among leaders." },
  { title: "Therapy, not team building", copy: "Guided by accredited Mindtale psychotherapists, focused on clarity, self-regulation and decision-making." },
  { title: "Nature with high standards", copy: "Reconnect Glamping offers premium comfort and authentic quiet so you can step out of crisis mode." },
];

const stats = [
  { value: "77%", label: "of employees have experienced burnout at least once", source: "Deloitte, 2022" },
  { value: "34%", label: "average productivity reduction in teams with chronic burnout", source: "Gallup" },
  { value: "63%", label: "of lost workdays are linked to stress and exhaustion", source: "APA" },
  { value: "1.5×", label: "annual salary cost to replace an employee who leaves due to burnout", source: "Gallup" },
];

function parseValue(value: string) {
  const match = value.match(/^([\d.]+)(.*)/);
  if (!match) return null;
  const raw = match[1];
  return { num: parseFloat(raw), suffix: match[2], decimals: raw.includes(".") ? raw.split(".")[1].length : 0 };
}

function AnimatedStat({ value, label, source }: (typeof stats)[number]) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  const parsed = parseValue(value);
  const [displayed, setDisplayed] = useState(parsed ? (0).toFixed(parsed.decimals) + parsed.suffix : value);

  useEffect(() => {
    if (!isInView || !parsed) return;
    const controls = animate(0, parsed.num, {
      duration: 1.8, ease: "easeOut",
      onUpdate: (v) => setDisplayed(v.toFixed(parsed.decimals) + parsed.suffix),
    });
    return () => controls.stop();
  }, [isInView]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={ref} className="h-full rounded-3xl border border-[#17352f]/10 bg-[#f8f4ed] p-6 shadow-[0_10px_36px_rgba(17,35,31,0.06)]">
      <p className="text-4xl font-semibold leading-none tracking-tight text-[#17352f]">{displayed}</p>
      <p className="mt-3 text-sm leading-relaxed text-[#2d4e47]/75">{label}</p>
      <p className="mt-4 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-[#2d6158]/58">{source}</p>
    </div>
  );
}

export default function ReMindStatsSectionEn() {
  return (
    <section id="retreat-overview" className="relative z-40 bg-[#f6f2eb] pb-20 pt-8 text-[#1e352f] sm:pb-24 sm:pt-10">
      <div className="pointer-events-none absolute -left-24 top-24 h-64 w-64 rounded-full bg-[#2d6158]/8 blur-3xl" />
      <div className="pointer-events-none absolute -right-16 top-56 h-72 w-72 rounded-full bg-[#17352f]/10 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.08] [mask-image:linear-gradient(to_bottom,black,transparent_85%)]" style={{ backgroundImage: "radial-gradient(#17352f 0.5px, transparent 0.5px)", backgroundSize: "6px 6px" }} />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2rem] border border-white/70 bg-[#f8f4ee]/95 shadow-[0_30px_110px_rgba(17,35,31,0.18)] backdrop-blur-xl">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(115deg,rgba(23,53,47,0.05),transparent_42%,rgba(23,53,47,0.03))]" />
          <div className="grid lg:grid-cols-[1.25fr_0.75fr]">
            <div className="relative p-8 sm:p-10 lg:p-12">
              <p className="text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#2d6158]/70">ReMind · Corporate Retreat</p>
              <h2 className={`${displaySerif.className} mt-4 text-3xl leading-[1.04] tracking-tight text-[#10221e] sm:text-4xl lg:text-[3.1rem]`}>
                Leaders perform better when they regain clarity.
              </h2>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#2d4e47]/78 sm:text-base">
                The ReMind program is designed for founders, executives and managers who operate under constant pressure. This is not an image retreat. It is a recalibration process with real impact on decision-making, relationships and energy.
              </p>
            </div>
            <div className="flex h-full flex-col border-t border-[#17352f]/12 bg-[#16302a] p-8 text-white lg:border-l lg:border-t-0 sm:p-10 lg:p-12">
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.26em] text-white/52">Executive Format</p>
              <ul className="mt-6 flex-1 space-y-4">
                {["3 days / 2 nights in a small cohort", "maximum 5 participants per session", "facilitated exclusively by psychotherapists", "individual accommodation and meals included", "debrief + post-retreat integration plan"].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-white/78">
                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#78b2a4]" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-7 border-t border-white/12 pt-6">
                <p className="mb-3 text-xs text-white/68">Want a version tailored to your team?</p>
                <a href="#contact" className="inline-flex items-center justify-center rounded-full bg-white px-5 py-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-[#17352f] transition-colors duration-200 hover:bg-white/90">
                  Request a quote
                </a>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 border-t border-[#17352f]/10 p-6 sm:grid-cols-4 sm:p-8">
            {stats.map((s) => <AnimatedStat key={s.label} {...s} />)}
          </div>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-3">
          {pillars.map((p, i) => (
            <FadeIn key={p.title} delay={i * 100}>
              <div className="rounded-2xl border border-[#17352f]/10 bg-[#f8f4ed] p-6">
                <p className="font-semibold text-[#132a25]">{p.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-[#2d4e47]/74">{p.copy}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn className="mt-10">
          <div className="relative overflow-hidden rounded-[2rem] bg-[#17352f]">
            <div className="relative h-64 sm:h-80">
              <Image src="/reconnect/2.jpg" alt="Reconnect Glamping cabins" fill className="object-cover object-center opacity-40" sizes="(max-width: 1280px) 100vw, 1152px" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#17352f] via-[#17352f]/80 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-center p-8 sm:p-12 lg:p-16">
                <p className="text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#78b2a4]/80">The Setting</p>
                <h3 className={`${displaySerif.className} mt-3 max-w-lg text-3xl font-semibold leading-tight text-white sm:text-4xl`}>
                  Reconnect Glamping, where the setting becomes part of the work.
                </h3>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}
